/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// 2) Third-party library imports
import {
	get,
	find,
	split
} from 'lodash-es';
import {
	createSelector,
	createSlice,
	PayloadAction
} from '@reduxjs/toolkit';
import { App } from 'antd';

// 3a) Constants
import { targetContentMap } from '../../constants/contents';

// 3d) Utility functions / interfaces
import { DynamicObject } from '@utils/commonInterfaces';

// 3e) Redux actions & types
import {
	ContentSelection,
	ContentTemplate,
	ContentTemplateConfiguration,
	ContentsState,
	GetContentTemplatePayload,
	GetContentTemplatePreviewPayload,
	ManualCommunicationTemplate,
	SaveContentTemplatePayload,
	WorkflowContentConfiguration,
	TemplateType,
	ManualCommunicationTemplateFile,
} from './types';
import type { RootState } from '../storeTypes';
import { selectUserCompanySuccessAction } from '../companies/companySlice';

export const initialState: ContentsState = {
	saveLoading: false,
	activeData: {
		errorMessages: [],
		previewErrors: [],
		loading: false,
		rendering: false,
		record: null,
		preview: {
			content: null,
			version: null
		},
		selection: null
	},
	manualContents: {
		errorMessages: [],
		loading: false,
		templates: null
	},
	otherContents: {
		errorMessages: [],
		loading: false,
		contentConfig: null
	},
	workflowContents: {
		errorMessages: [],
		loading: false,
		contentConfigs: null
	}
};

const contentSlice = createSlice({
	name: 'content',
	initialState,
	reducers: {
		getWorkflowContentConfigurationAction: (state) => {
			state.workflowContents.loading = true;
		},
		getWorkflowContentConfigurationSuccess: (
			state,
			action: PayloadAction<WorkflowContentConfiguration[]>
		) => {
			state.workflowContents.loading = false;
			state.workflowContents.contentConfigs = action.payload;
			state.workflowContents.errorMessages = []
		},
		getWorkflowContentConfigurationError: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.workflowContents.loading = false;
			state.workflowContents.contentConfigs = null;
			state.workflowContents.errorMessages = action.payload;
		},
		clearWorkflowContentConfiguration: (
			state
		) => {
			state.workflowContents = initialState.workflowContents;
		},
		getContentTemplateConfigurationAction: (state) => {
			state.otherContents.loading = true;
		},
		getContentTemplateConfigurationSuccess: (
			state,
			action: PayloadAction<ContentTemplateConfiguration>
		) => {
			state.otherContents.loading = false;
			state.otherContents.contentConfig = action.payload;
			state.otherContents.errorMessages = [];
		},
		getContentTemplateConfigurationError: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.otherContents.loading = false;
			state.otherContents.contentConfig = null;
			state.otherContents.errorMessages = action.payload;
		},
		getManualCommunicationTemplatesAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					callback?: (response: ManualCommunicationTemplate[]) => void;
					modal?: ReturnType<typeof App.useApp>['modal'],
				}>
			) => {
				state.manualContents.loading = true;
			},
			prepare: (
				callback?: (response: ManualCommunicationTemplate[]) => void,
				modal?: ReturnType<typeof App.useApp>['modal'],
			) => ({
				payload: {
					callback,
					modal
				}
			})
		},
		getManualCommunicationTemplatesActionSuccess: (
			state,
			action: PayloadAction<ManualCommunicationTemplate[]>
		) => {
			state.manualContents.loading = false;
			state.manualContents.templates = action.payload;
			state.manualContents.errorMessages = [];
		},
		getManualCommunicationTemplatesActionError: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.manualContents.loading = false;
			state.manualContents.templates = null;
			state.manualContents.errorMessages = action.payload;
		},
		getContentTemplateAction: (
			state,
			action: PayloadAction<GetContentTemplatePayload>
		) => {
			state.activeData.loading = true;
			state.activeData.record = null;
		},
		getContentTemplateSuccess: (
			state,
			action: PayloadAction<ContentTemplate>
		) => {
			state.activeData.loading = false;
			state.activeData.record = action.payload;
			state.activeData.errorMessages = [];
			state.activeData.preview.content = get(action.payload, 'Preview');
		},
		getContentTemplateError: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.activeData.loading = false;
			state.activeData.record = null;
			state.activeData.errorMessages = action.payload;
		},
		getContentTemplatePreviewAction: (
			state,
			_action: PayloadAction<{
				payload: GetContentTemplatePreviewPayload;
				version: string;
			}>
		) => {
			state.activeData.rendering = true;
		},
		getContentTemplatePreviewSuccess: (
			state,
			action: PayloadAction<ContentsState['activeData']['preview']>
		) => {
			if (action.payload.version === state.activeData.preview.version) {
				state.activeData.rendering = false;
				state.activeData.preview = action.payload;
				state.activeData.previewErrors = [];
			}
		},
		getContentTemplatePreviewError: (
			state,
			action: PayloadAction<{
				previewErrors: string[];
				version: string | null;
			}>
		) => {
			if (action.payload.version === state.activeData.preview.version) {
				state.activeData.rendering = false;
				state.activeData.previewErrors = action.payload.previewErrors;
			}
		},
		saveContentTemplateAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					payload: SaveContentTemplatePayload;
					callback?: (response: DynamicObject) => void;
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				payload: SaveContentTemplatePayload,
				callback?: (response: DynamicObject) => void
			) => ({
				payload: { payload, callback }
			})
		},
		saveContentTemplateSuccess: (
			state,
			action: PayloadAction<SaveContentTemplatePayload & { NewTemplateKey: string }>
		) => {
			const { WorkflowId, StateName, TemplateContentName, NewTemplateKey, Type, ...props } = action.payload;
			
			switch (Type) {
				case TemplateType.WorkflowContent: {
					const workflowStateConfigs = state.workflowContents.contentConfigs;
					const targetContentName: string = find(Object.keys(props), (key) => key in targetContentMap && (props as Record<string, any>)[key]) as string;
					const templateKeyName = get(targetContentMap[targetContentName], 'templateKeyName');

					// Create fully immutable update - new references at all levels
					if (workflowStateConfigs && templateKeyName) {
						state.workflowContents.contentConfigs = workflowStateConfigs.map(workflow => {
							if (workflow.WorkflowId !== WorkflowId) {
								return workflow;
							}
							// Create new workflow object with updated States
							return {
								...workflow,
								States: workflow.States.map(stateItem => {
									if (stateItem.StateName !== StateName) {
										return stateItem;
									}
									// Create new state object with updated template key
									return {
										...stateItem,
										[templateKeyName]: NewTemplateKey
									};
								})
							};
						});
					}

					state.saveLoading = false;
					break;
				}
				case TemplateType.ManualContent: {
					const manualTemplates = state.manualContents.templates;
					const templateContent = find(manualTemplates, c => c.TemplateName === TemplateContentName);
					const targetContentName: string = find(Object.keys(props), key => key in targetContentMap && (props as Record<string, any>)[key]) as string;
					const fileType = get(targetContentMap[targetContentName], 'fileType');
					const fileIndex = get(templateContent, 'Files', []).findIndex(f => f.Type === fileType);

					if (fileIndex !== -1 && templateContent) {
						// Create a new file object instead of modifying the original one
						const updatedFile: ManualCommunicationTemplateFile = {
							...templateContent.Files[fileIndex],
							// Extracts last part of NewTemplateKey
							Name: split(NewTemplateKey, '/').pop() ?? "" // Ensure 'Name' is never undefined
						};

						// Update only the saved file, preserving all other files in the array
						templateContent.Files = [
							...templateContent.Files.slice(0, fileIndex),
							updatedFile,
							...templateContent.Files.slice(fileIndex + 1)
						];
					}

					state.saveLoading = false;
					state.manualContents.templates = manualTemplates ? [...manualTemplates] : [];
					break;
				}
				case TemplateType.Others: {
					const contentTemplateConfig = state.otherContents.contentConfig;
					const templateContents = get(contentTemplateConfig, 'TemplateContents', []);

					// Find the target template content
					const templateIndex = templateContents.findIndex((c: { TemplateName: string }) => c.TemplateName === TemplateContentName);

					if (templateIndex !== -1) {
						// Create a new array with updated object (immutability)
						const updatedTemplateContent = {
							...templateContents[templateIndex],
							TemplateContentS3Key: NewTemplateKey
						};

						const updatedTemplateContents = [
							...templateContents.slice(0, templateIndex),
							updatedTemplateContent,
							...templateContents.slice(templateIndex + 1)
						];

						// Update contentConfig immutably
						state.otherContents.contentConfig = {
							...contentTemplateConfig,
							TemplateContents: updatedTemplateContents
						};
					}

					state.saveLoading = false;
					break;
				}
			}
		},
		saveContentTemplateError: (state) => {
			state.saveLoading = false;
		},
		changeContentSelectionAction: (
			state,
			action: PayloadAction<ContentSelection>
		) => {
			state.activeData.selection = action.payload;
		},
		changePreviewVersionAction: (
			state,
			action: PayloadAction<string>
		) => {
			state.activeData.preview.version = action.payload;

			state.activeData.previewErrors = [];
		},
		clearContentStateAction: (state) => {
			Object.assign(state, initialState);
		},
		uploadImageForEditorAction: {
			reducer: (
				_state,
				_action: PayloadAction<{
					fileName: string;
					uploadId: string;
					base64Data: string;
					contentType: string;
					companyId: string;
					callback?: (response: DynamicObject) => void;
				}>
			) => {
				// Handled by saga
			},
			prepare: (
				fileName: string,
				uploadId: string,
				base64Data: string,
				contentType: string,
				companyId: string,
				callback?: (response: DynamicObject) => void
			) => ({
				payload: {
					fileName,
					uploadId,
					base64Data,
					contentType,
					companyId,
					callback
				}
			})
		}
	},
	extraReducers: (builder) => {
		// Reset contents state to it initial state
		builder.addCase(selectUserCompanySuccessAction, (state) => { Object.assign(state, initialState); });
	},
});

const { actions, reducer } = contentSlice;

// Actions
export const {
	getWorkflowContentConfigurationAction,
	getWorkflowContentConfigurationSuccess,
	getWorkflowContentConfigurationError,
	clearWorkflowContentConfiguration,
	getContentTemplateConfigurationAction,
	getContentTemplateConfigurationSuccess,
	getContentTemplateConfigurationError,
	getManualCommunicationTemplatesAction,
	getManualCommunicationTemplatesActionSuccess,
	getManualCommunicationTemplatesActionError,
	getContentTemplateAction,
	getContentTemplateSuccess,
	getContentTemplateError,
	getContentTemplatePreviewAction,
	getContentTemplatePreviewSuccess,
	getContentTemplatePreviewError,
	saveContentTemplateAction,
	saveContentTemplateSuccess,
	saveContentTemplateError,
	changeContentSelectionAction,
	changePreviewVersionAction,
	clearContentStateAction,
	uploadImageForEditorAction
} = actions;

// Selectors
export const getContentsState = (state: RootState) => state.contents;
export const getManualTemplates = createSelector(
	getContentsState,
	(contents) => contents.manualContents.templates
);
export const getContentTemplateConfig = createSelector(
	getContentsState,
	(contents) => contents.otherContents.contentConfig
);
export const getWorkflowContent = createSelector(
	getContentsState,
	(contents) => contents.workflowContents
);
export const getWorkflowStateConfigs = createSelector(
	getWorkflowContent,
	(workflowContents) => workflowContents.contentConfigs
);
export const getContentData = createSelector(
	getContentsState,
	(contents) => contents.activeData
);
export const getContentSelection = createSelector(
	getContentData,
	(activeData) => activeData.selection
);
export const getSelectedContentTemplate = createSelector(
	getContentData,
	(activeData) => activeData.record
);
export const getPreview = createSelector(
	getContentData,
	(activeData) => activeData.preview
);

// Reducer
export default reducer;